import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGuildPatch, useGuildConfig } from '../../../lib/auditQueries.js';
import { Breadcrumbs } from '../../../components/Breadcrumbs.js';
import { LanguageSwitcher } from '../../../components/LanguageSwitcher.js';
import { ChannelPicker } from '../../../components/ChannelPicker.js';
import {
  useUpdateWelcome,
  useWelcome,
  useWelcomePreview,
  type PreviewResponse,
  type WelcomeConfig,
} from '../../../lib/welcomeQueries.js';

export const Route = createFileRoute('/guilds/$guildId/welcome')({
  component: WelcomePage,
});

type Kind = 'welcome' | 'goodbye';

interface FormState {
  welcomeChannelId: string;
  welcomeMessage: string;
  welcomeUseEmbed: boolean;
  welcomeEmbedColor: string;
  goodbyeChannelId: string;
  goodbyeMessage: string;
  goodbyeUseEmbed: boolean;
  goodbyeEmbedColor: string;
}

function fromConfig(c: WelcomeConfig): FormState {
  return {
    welcomeChannelId: c.welcomeChannelId ?? '',
    welcomeMessage: c.welcomeMessage ?? '',
    welcomeUseEmbed: c.welcomeUseEmbed,
    welcomeEmbedColor: c.welcomeEmbedColor ?? '#6366f1',
    goodbyeChannelId: c.goodbyeChannelId ?? '',
    goodbyeMessage: c.goodbyeMessage ?? '',
    goodbyeUseEmbed: c.goodbyeUseEmbed,
    goodbyeEmbedColor: c.goodbyeEmbedColor ?? '#6366f1',
  };
}

function toBody(state: FormState): Omit<WelcomeConfig, 'guildId'> {
  return {
    welcomeChannelId: state.welcomeChannelId || null,
    welcomeMessage: state.welcomeMessage || null,
    welcomeUseEmbed: state.welcomeUseEmbed,
    welcomeEmbedColor: state.welcomeEmbedColor || null,
    goodbyeChannelId: state.goodbyeChannelId || null,
    goodbyeMessage: state.goodbyeMessage || null,
    goodbyeUseEmbed: state.goodbyeUseEmbed,
    goodbyeEmbedColor: state.goodbyeEmbedColor || null,
  };
}

function WelcomePage() {
  const { t } = useTranslation();
  const { guildId } = Route.useParams();
  const [kind, setKind] = useState<Kind>('welcome');
  const welcome = useWelcome(guildId);
  const guildConfig = useGuildConfig(guildId);
  const guildPatch = useGuildPatch(guildId);
  const update = useUpdateWelcome(guildId);
  const preview = useWelcomePreview(guildId);

  const [state, setState] = useState<FormState | null>(null);
  const [previewData, setPreviewData] = useState<PreviewResponse | null>(null);
  const previewTimer = useRef<number | null>(null);

  // Hydrate form state when the welcome query lands.
  useEffect(() => {
    if (welcome.data && !state) {
      setState(fromConfig(welcome.data.config));
    }
  }, [welcome.data, state]);

  // Debounced live preview — re-fetch whenever the kind or saved config changes.
  useEffect(() => {
    if (!state) return;
    if (previewTimer.current) window.clearTimeout(previewTimer.current);
    previewTimer.current = window.setTimeout(() => {
      preview.mutate(kind, {
        onSuccess: (data) => {
          setPreviewData(data);
        },
        onError: () => {
          setPreviewData(null);
        },
      });
    }, 400);
    return () => {
      if (previewTimer.current) window.clearTimeout(previewTimer.current);
    };
  }, [kind, welcome.data?.config, preview, state]);

  if (!state) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100">
        <Header guildId={guildId} />
        <p className="mx-auto max-w-6xl px-6 py-8 text-sm text-zinc-400">{t('common.loading')}</p>
      </div>
    );
  }

  const isWelcome = kind === 'welcome';
  const channelId = isWelcome ? state.welcomeChannelId : state.goodbyeChannelId;
  const message = isWelcome ? state.welcomeMessage : state.goodbyeMessage;
  const useEmbed = isWelcome ? state.welcomeUseEmbed : state.goodbyeUseEmbed;
  const embedColor = isWelcome ? state.welcomeEmbedColor : state.goodbyeEmbedColor;
  const featureEnabled = isWelcome
    ? (guildConfig.data?.welcomeEnabled ?? false)
    : (guildConfig.data?.goodbyeEnabled ?? false);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Header guildId={guildId} />
      <main className="mx-auto grid max-w-6xl grid-cols-12 gap-6 px-6 py-8">
        <div className="col-span-12 lg:col-span-7">
          <Tabs kind={kind} setKind={setKind} />

          <label className="mb-4 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-zinc-700 bg-zinc-900"
              checked={featureEnabled}
              onChange={(e) => {
                guildPatch.mutate(
                  isWelcome
                    ? { welcomeEnabled: e.target.checked }
                    : { goodbyeEnabled: e.target.checked },
                );
              }}
            />
            {t(`welcome.${kind}.toggle`)}
          </label>

          <div className="space-y-4 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
            <Field label={t('welcome.field.channel')}>
              <ChannelPicker
                guildId={guildId}
                value={channelId}
                onChange={(v) => {
                  setState((s) =>
                    s
                      ? isWelcome
                        ? { ...s, welcomeChannelId: v }
                        : { ...s, goodbyeChannelId: v }
                      : s,
                  );
                }}
                placeholder={t('welcome.field.channelPick')}
              />
            </Field>

            <Field
              label={t('welcome.field.message')}
              hint={t('welcome.field.messageHint', {
                vars: '{user}, {username}, {server}, {membercount}',
              })}
            >
              <textarea
                className="min-h-32 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 font-mono text-sm"
                value={message}
                onChange={(e) => {
                  const v = e.target.value;
                  setState((s) =>
                    s ? (isWelcome ? { ...s, welcomeMessage: v } : { ...s, goodbyeMessage: v }) : s,
                  );
                }}
                placeholder={t('welcome.field.messagePlaceholder')}
                maxLength={2000}
              />
            </Field>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-zinc-700 bg-zinc-900"
                  checked={useEmbed}
                  onChange={(e) => {
                    const v = e.target.checked;
                    setState((s) =>
                      s
                        ? isWelcome
                          ? { ...s, welcomeUseEmbed: v }
                          : { ...s, goodbyeUseEmbed: v }
                        : s,
                    );
                  }}
                />
                {t('welcome.field.useEmbed')}
              </label>
              {useEmbed ? (
                <label className="flex items-center gap-2 text-sm">
                  <span className="text-zinc-400">{t('welcome.field.color')}</span>
                  <input
                    type="color"
                    className="h-7 w-12 cursor-pointer rounded border border-zinc-800 bg-zinc-950"
                    value={embedColor}
                    onChange={(e) => {
                      const v = e.target.value;
                      setState((s) =>
                        s
                          ? isWelcome
                            ? { ...s, welcomeEmbedColor: v }
                            : { ...s, goodbyeEmbedColor: v }
                          : s,
                      );
                    }}
                  />
                </label>
              ) : null}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  if (welcome.data) setState(fromConfig(welcome.data.config));
                }}
                className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-800"
              >
                {t('welcome.button.reset')}
              </button>
              <button
                type="button"
                disabled={update.isPending}
                onClick={() => {
                  update.mutate(toBody(state));
                }}
                className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
              >
                {update.isPending ? t('common.loading') : t('welcome.button.save')}
              </button>
            </div>
            {update.isError ? (
              <p className="text-xs text-rose-400">
                {update.error instanceof Error ? update.error.message : t('common.error')}
              </p>
            ) : null}
            {update.isSuccess ? (
              <p className="text-xs text-emerald-400">{t('welcome.saved')}</p>
            ) : null}
          </div>
        </div>

        <aside className="col-span-12 lg:col-span-5">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">
            {t('welcome.preview.title')}
          </h3>
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
            {previewData?.rendered ? (
              previewData.useEmbed ? (
                <div
                  className="rounded-md border-l-4 bg-zinc-950 p-3"
                  style={{ borderLeftColor: previewData.embedColor ?? '#6366f1' }}
                >
                  <p className="whitespace-pre-wrap text-sm">{previewData.rendered}</p>
                </div>
              ) : (
                <p className="whitespace-pre-wrap rounded-md bg-zinc-950 p-3 text-sm">
                  {previewData.rendered}
                </p>
              )
            ) : (
              <p className="text-sm text-zinc-500">{t('welcome.preview.empty')}</p>
            )}
            <p className="mt-3 text-xs text-zinc-500">{t('welcome.preview.note')}</p>
          </div>
        </aside>
      </main>
    </div>
  );
}

function Header({ guildId }: { guildId: string }) {
  const { t } = useTranslation();
  const guildConfig = useGuildConfig(guildId);
  return (
    <header className="border-b border-zinc-800">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-4">
        <Link to="/" className="shrink-0">
          <img src="/icon.png" alt="Frankenstein" className="h-6 w-6" />
        </Link>
        <Breadcrumbs
          items={[
            { label: t('app.name'), to: '/' },
            {
              label: guildConfig.data?.name ?? guildId,
              to: '/guilds/$guildId',
              params: { guildId },
            },
            { label: t('welcome.title') },
          ]}
        />
        <div className="ml-auto">
          <LanguageSwitcher />
        </div>
      </div>
    </header>
  );
}

function Tabs({ kind, setKind }: { kind: Kind; setKind: (k: Kind) => void }) {
  const { t } = useTranslation();
  return (
    <div className="mb-4 flex gap-1 rounded-md border border-zinc-800 bg-zinc-900/40 p-1">
      {(['welcome', 'goodbye'] as const).map((k) => (
        <button
          key={k}
          type="button"
          onClick={() => {
            setKind(k);
          }}
          className={`flex-1 rounded px-3 py-1.5 text-sm transition-colors ${
            kind === k ? 'bg-indigo-600 text-white' : 'text-zinc-300 hover:bg-zinc-800/60'
          }`}
        >
          {t(`welcome.${k}.tab`)}
        </button>
      ))}
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs text-zinc-400">{label}</label>
      {children}
      {hint ? <p className="mt-1 text-xs text-zinc-500">{hint}</p> : null}
    </div>
  );
}
